import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Columns } from '@phosphor-icons/react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL || '';
const DAY_LABELS = { segunda: 'Segunda', terca: 'Terça', quarta: 'Quarta', quinta: 'Quinta', sexta: 'Sexta', sabado: 'Sábado', domingo: 'Domingo' };

function MenuColumn({ patients, patientId, setPatientId, weekId, setWeekId, weeks, setWeeks, testPrefix }) {
  const week = weeks.find(w => w.id === weekId);
  const fetchWeeks = useCallback(async (pid) => {
    if (!pid) { setWeeks([]); return; }
    try { const { data } = await axios.get(`${API}/api/patients/${pid}/baby-menus`); setWeeks(data || []); setWeekId(data?.[0]?.id || ''); } catch { setWeeks([]); }
  }, [setWeeks, setWeekId]);

  useEffect(() => { fetchWeeks(patientId); }, [patientId, fetchWeeks]);

  return (
    <div className="flex-1 min-w-0 bg-white border border-brand-border rounded-2xl p-4" data-testid={`${testPrefix}-column`}>
      <Select value={patientId} onValueChange={setPatientId}>
        <SelectTrigger className="mb-2" data-testid={`${testPrefix}-patient-select`}><SelectValue placeholder="Escolhe uma paciente" /></SelectTrigger>
        <SelectContent>{patients.map(p => <SelectItem key={p.user_id} value={p.user_id}>{p.name}</SelectItem>)}</SelectContent>
      </Select>
      {weeks.length > 0 && (
        <Select value={weekId} onValueChange={setWeekId}>
          <SelectTrigger className="mb-3" data-testid={`${testPrefix}-week-select`}><SelectValue placeholder="Semana" /></SelectTrigger>
          <SelectContent>{weeks.map(w => <SelectItem key={w.id} value={w.id}>{w.label} {w.focus ? `- ${w.focus}` : ''}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {!patientId ? (
        <p className="text-xs text-brand-placeholder text-center py-8">Escolhe uma paciente para ver o menu.</p>
      ) : !week ? (
        <p className="text-xs text-brand-placeholder text-center py-8">Sem menus de bebé para esta paciente.</p>
      ) : (
        <div className="space-y-3">
          {Object.entries(week.days || {}).filter(([, meals]) => Array.isArray(meals) && meals.length > 0).map(([day, meals]) => (
            <div key={day}>
              <p className="text-[10px] uppercase tracking-wider text-brand-muted mb-1">{DAY_LABELS[day] || day}</p>
              <div className="space-y-1.5">
                {meals.map((meal, i) => (
                  <div key={i} className="bg-brand-bg/50 rounded-xl p-2">
                    <p className="text-[11px] font-medium text-brand-text">{meal.type}</p>
                    {(meal.items || []).length > 0 ? (
                      <p className="text-[11px] text-brand-muted">{meal.items.map(it => it.food).join(', ')}</p>
                    ) : (meal.info_lines || []).map((l, li) => <p key={li} className="text-[11px] text-brand-muted italic">{l}</p>)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CompararMenusBebePage() {
  const [patients, setPatients] = useState([]);
  const [patientA, setPatientA] = useState(''); const [weekA, setWeekA] = useState(''); const [weeksA, setWeeksA] = useState([]);
  const [patientB, setPatientB] = useState(''); const [weekB, setWeekB] = useState(''); const [weeksB, setWeeksB] = useState([]);

  useEffect(() => {
    (async () => { try { const { data } = await axios.get(`${API}/api/patients?search=`); setPatients(data || []); } catch { toast.error('Erro ao carregar pacientes'); } })();
  }, []);

  return (
    <div className="fade-in" data-testid="comparar-menus-bebe-page">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 rounded-2xl bg-indigo-100 flex items-center justify-center"><Columns size={22} className="text-indigo-600" weight="duotone" /></div>
        <div>
          <h2 className="font-heading font-medium text-2xl text-brand-text">Comparar Menus de Bebé</h2>
          <p className="text-sm text-brand-muted mt-0.5">Vê lado a lado os menus de duas pacientes para comparar grupos alimentares e progressão.</p>
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-4">
        <MenuColumn patients={patients} patientId={patientA} setPatientId={setPatientA} weekId={weekA} setWeekId={setWeekA} weeks={weeksA} setWeeks={setWeeksA} testPrefix="compare-a" />
        <MenuColumn patients={patients} patientId={patientB} setPatientId={setPatientB} weekId={weekB} setWeekId={setWeekB} weeks={weeksB} setWeeks={setWeeksB} testPrefix="compare-b" />
      </div>
    </div>
  );
}
